import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  SwatchIcon,
  ClipboardDocumentCheckIcon,
  CogIcon,
  TruckIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

const Services = () => {
  const [expandedIndex, setExpandedIndex] = useState(null);

  const toggleExpand = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const steps = [
    {
      icon: <SwatchIcon className="h-8 w-8" />,
      title: 'Design & Planning',
      description: 'Concept development and technical specifications',
      details:
        'From concept to creation, we bring your fashion vision to life with our expert design team, ensuring every design is precise.',
    },
    {
      icon: <ClipboardDocumentCheckIcon className="h-8 w-8" />,
      title: 'Sampling',
      description: 'Creating prototypes and sample approval',
      details:
        'Our prototype development involves fabric selection, pattern making, and style consultation to ensure your product meets expectations.',
    },
    {
      icon: <CogIcon className="h-8 w-8" />,
      title: 'Production',
      description: 'Bulk manufacturing with quality control',
      details:
        'State-of-the-art manufacturing facilities, skilled workforce, and rigorous quality control ensure your products are top-notch in both knits and wovens.',
    },
    {
      icon: <TruckIcon className="h-8 w-8" />,
      title: 'Delivery',
      description: 'Packaging and global shipping',
      details:
        'Efficient supply chain management and worldwide shipping ensure timely delivery and satisfaction.',
    },
  ];

  return (
    <section
      id="services"
      className="py-12 lg:py-12 bg-gradient-to-br from-honeycomb-grey-900 to-honeycomb-blue-900 relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center px-4 py-2 bg-honeycomb-blue-600/20 text-honeycomb-blue-200 rounded-full text-sm font-medium mb-6">
            Our Services
          </div>
          <h2 className="text-4xl lg:text-5xl font-display font-bold text-white mb-6">
            Comprehensive Manufacturing <span className="text-honeycomb-blue-400">Process</span>
          </h2>
          <p className="text-lg text-honeycomb-blue-100 max-w-3xl mx-auto">
            End-to-end garment manufacturing services designed to bring your fashion ideas to life.
            From <span className="text-white font-semibold"> sketch to shipment</span>, we handle every aspect of the production process.
          </p>
        </motion.div>

        {/* Steps Grid with Connector Lines */}
        <motion.div
          layout
          className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {steps.map((step, index) => (
            <motion.div
              key={step.title}
              layout
              transition={{ layout: { duration: 0.6, type: 'spring' } }}
              className={`group relative bg-white/10 backdrop-blur-md p-8 rounded-2xl border border-white/20 cursor-pointer ${
                expandedIndex === index ? 'bg-white/20' : 'hover:bg-white/15'
              }`}
              onClick={() => toggleExpand(index)}
            >
              {/* Connector Line - show for all but last, only desktop */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute right-0 top-1/2 w-10 h-[2px] bg-honeycomb-blue-400/70 translate-x-full -translate-y-1/2"></div>
              )}

              {/* Icon */}
              <div className="w-16 h-16 bg-honeycomb-blue-600 rounded-xl flex items-center justify-center text-white mb-6 z-10 relative">
                {step.icon}
              </div>

              {/* Title & Description */}
              <h3 className="text-2xl font-semibold text-white group-hover:text-honeycomb-blue-200 transition-colors">
                {step.title}
              </h3>
              <p className="text-honeycomb-blue-100 leading-relaxed mt-3">
                {step.description}
              </p>

              {/* Expandable Details */}
              <AnimatePresence>
                {expandedIndex === index && (
                  <motion.div
                    key="expanded"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.4, ease: 'easeInOut' }}
                    className="mt-4 overflow-hidden text-honeycomb-grey-100 text-sm"
                  >
                    {step.details}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Arrow */}
              <div className="mt-4 flex items-center text-honeycomb-blue-400 font-medium select-none">
                {expandedIndex === index ? 'Hide Details' : 'Learn More'}
                <svg
                  className={`ml-2 w-4 h-4 transition-transform duration-300 ${
                    expandedIndex === index ? 'rotate-90' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
          className="text-center mt-16"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-honeycomb-blue-600 text-white px-8 py-4 rounded-full font-semibold hover:bg-honeycomb-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl btn-primary"
            onClick={() => {
              document.querySelector('#contact')?.scrollIntoView({
                behavior: 'smooth',
              });
            }}
          >
            Start Your Project Today
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
